import { useCallback, useContext } from 'react';

import { StorefrontContentContext } from '@/features/storefront/content/context';
import { storefrontLocaleDictionary } from '@/features/storefront/content/localeLoader';
import { SHOP_NAMESPACE, contentKey } from '@/features/storefront/content/types';
import { useStorefrontLanguage } from '@/features/storefront/i18n/context';
import { StorefrontThemeContext } from '@/features/storefront/storefrontThemeContext';
import { humaniseKey } from '@/lib/i18n/translate';
import { CONTENT_DEFAULTS } from '@/previewThemes/content';

/** Nothing customised. One frozen object, so it is never a new dependency. */
const NONE: Readonly<Record<string, string>> = {};

/** What a piece of copy is read with: a key, and anything to fill into it. */
export type ContentReader = (key: string, vars?: Record<string, string | number>) => string;

/**
 * Fill `{name}` placeholders from the values a component has to hand.
 *
 * Deliberately the plainest substitution that works. A merchant writing
 * "Only {available} left" is naming a number the page already knows, not
 * writing a template language, and a placeholder nobody supplied is left
 * standing rather than silently blanked - a visible `{available}` in a preview
 * is a merchant's own typo pointed at, which is more useful than a sentence
 * with a hole in it.
 */
function fill(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  );
}

/**
 * One namespace's copy, resolved for the merchant whose shop is rendering.
 *
 * `useContent('hyper')` in a Hyper component, `useShopContent()` in a shared
 * one. What comes back reads a key and answers with the merchant's words if
 * they have written any and the theme's if they have not - which is the whole
 * of the fallback rule, in one place, rather than a `??` at three hundred
 * render sites.
 *
 * A stored blank is treated as "not customised" rather than as an empty
 * string. The editor deletes a key when its field is cleared, so a blank
 * should not be reachable at all; if one arrives from a hand-written document
 * it resolves to the theme's words, because a shop with an unexplained hole in
 * its checkout is worse than one whose copy a merchant meant to remove and
 * could not.
 */
export function useContent(namespace: string): ContentReader {
  // Three context reads and no query, deliberately: this runs in every label,
  // button and empty state on the shop, and it has to cost what reading a
  // constant costs. The shell's theme document is the usual source; the
  // explicit context is the customiser handing over a draft. See `context.ts`.
  const editing = useContext(StorefrontContentContext);
  const shellTheme = useContext(StorefrontThemeContext);
  const overrides = editing ?? shellTheme?.content ?? NONE;
  // The storefront language's built-in copy, already loaded by the provider
  // (it keeps the previous language on screen until it is). English, or a
  // shop with no provider, has none: the manifests' own words are English.
  const { code } = useStorefrontLanguage();
  const dictionary = storefrontLocaleDictionary(code) ?? NONE;

  return useCallback(
    (key, vars) => fill(resolveContent(contentKey(namespace, key), overrides, dictionary), vars),
    [overrides, dictionary, namespace],
  );
}

function present(value: string | undefined): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * One full key's words (docs/i18n.md §4): the merchant's override → the
 * built-in dictionary for the storefront language → the English default →
 * the key humanised, so a shopper never reads `shop.cart.title` or nothing.
 *
 * The override needs no language of its own here: the server already returns
 * the theme document localised, with that language's `content` overrides.
 */
export function resolveContent(
  full: string,
  overrides: Readonly<Record<string, string>>,
  dictionary: Readonly<Record<string, string>> = NONE,
): string {
  const override = overrides[full];
  if (present(override)) return override;
  const translated = dictionary[full];
  if (present(translated)) return translated;
  const english = CONTENT_DEFAULTS[full];
  if (present(english)) return english;
  return humaniseKey(full);
}

/** The storefront's own shared copy - the cart, the till, the empty shelves. */
export function useShopContent(): ContentReader {
  return useContent(SHOP_NAMESPACE);
}
